import { inject, Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { catchError, finalize, Observable, tap, throwError } from "rxjs";

import { TelemetryActions } from "../store/telemetry";

const OPERATION_DURATION_METRIC = "du.frontend.operation.duration";
const OPERATION_STARTED_EVENT = "du.frontend.operation.started";
const OPERATION_SUCCEEDED_EVENT = "du.frontend.operation.succeeded";
const OPERATION_FAILED_EVENT = "du.frontend.operation.failed";
const OPERATION_NAME_ATTR = "du.operation.name";
const EXCEPTION_MESSAGE_ATTR = "exception.message";
const EXCEPTION_TYPE_ATTR = "exception.type";

/**
 * TelemetryService provides a simplified facade over NgRx telemetry actions
 * for easier component integration and common telemetry patterns.
 *
 * Attribute/metric/event names follow the "Standardize Service Telemetry" ADR:
 * business attributes use the `du.*` namespace, exceptions use the OTel
 * semantic conventions (`exception.message`, `exception.type`).
 */
@Injectable({ providedIn: "root" })
export class TelemetryService {
  private store = inject(Store);

  /**
   * Track a custom event with optional properties
   * @param name Event name (e.g., 'du.frontend.document.upload.started')
   * @param properties Additional metadata about the event
   */
  trackEvent(name: string, properties?: Record<string, any>): void {
    this.store.dispatch(TelemetryActions.logEvent({ name, properties }));
  }

  /**
   * Track a performance metric
   * @param name Metric name (e.g., 'du.frontend.operation.duration')
   * @param value Numeric value (e.g., time in seconds)
   * @param properties Additional metadata about the metric
   */
  trackMetric(
    name: string,
    value: number,
    properties?: Record<string, any>
  ): void {
    this.store.dispatch(
      TelemetryActions.logMetric({
        name,
        average: value,
        properties,
      })
    );
  }

  /**
   * Track an exception/error
   * @param error Error object to track
   * @param severityLevel Optional severity (0=Verbose, 1=Information, 2=Warning, 3=Error, 4=Critical)
   */
  trackException(error: Error, severityLevel?: number): void {
    this.store.dispatch(
      TelemetryActions.logException({
        exception: error,
        severityLevel,
      })
    );
  }

  /**
   * Track a custom page view (beyond automatic routing)
   * @param name Page name
   * @param url Optional URL
   */
  trackPageView(name: string, url?: string): void {
    this.store.dispatch(TelemetryActions.logPageView({ name, url }));
  }

  /**
   * Track a trace/diagnostic message
   * @param message Trace message
   * @param properties Additional metadata
   */
  trackTrace(message: string, properties?: Record<string, any>): void {
    this.store.dispatch(TelemetryActions.logTrace({ message, properties }));
  }

  /**
   * Helper method to track timed operations. Emits a single
   * `du.frontend.operation.duration` metric tagged with `du.operation.name`,
   * keeping metric-name cardinality bounded per OTel conventions.
   *
   * @param operationName Logical name of the operation (e.g., 'document_upload')
   * @returns Function to call when operation completes - tracks duration
   */
  startTimer(operationName: string): () => void {
    const startTime = performance.now();
    return () => {
      const duration = (performance.now() - startTime) / 1000;
      this.trackMetric(OPERATION_DURATION_METRIC, duration, {
        [OPERATION_NAME_ATTR]: operationName,
      });
    };
  }

  /**
   * Track an Observable operation: emits
   * `du.frontend.operation.started/succeeded/failed` events and a duration
   * metric, all tagged with `du.operation.name`. Failures carry OTel
   * `exception.message` / `exception.type` attributes.
   */
  trackOperation<T>(
    operationName: string,
    operation: Observable<T>,
    properties?: Record<string, any>
  ): Observable<T> {
    const stopTimer = this.startTimer(operationName);
    let hasTrackedSuccess = false;
    const opProperties = {
      ...properties,
      [OPERATION_NAME_ATTR]: operationName,
    };

    this.trackEvent(OPERATION_STARTED_EVENT, opProperties);

    return operation.pipe(
      tap(() => {
        if (!hasTrackedSuccess) {
          hasTrackedSuccess = true;
          this.trackEvent(OPERATION_SUCCEEDED_EVENT, opProperties);
        }
      }),
      catchError((error: unknown) => {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        const errorType =
          error instanceof Error ? error.constructor.name : typeof error;
        this.trackEvent(OPERATION_FAILED_EVENT, {
          ...opProperties,
          [EXCEPTION_MESSAGE_ATTR]: errorMessage,
          [EXCEPTION_TYPE_ATTR]: errorType,
        });
        if (error instanceof Error) {
          this.trackException(error);
        }
        return throwError(() => error);
      }),
      finalize(() => {
        stopTimer();
      })
    );
  }
}
